/**
 * Shared Filter Type Definitions
 * Reusable types for report and list filters across the application
 */

// Core filter keys that can be enabled
export type FilterKey =
  | 'search'
  | 'textSearch'
  | 'fpStatus'
  | 'market'
  | 'region'
  | 'romFbc'
  | 'systemStatus'
  | 'adoptionStatus'
  | 'icuStatus'
  | 'sellingStatus'
  | 'dateRange'
  | 'system'
  | 'syncStatus'
  | 'initiatedBy'
  | 'connectionStatus'
  | 'exceptionTypes'
  | 'minGapDays'
  | 'minVarianceAmount'
  | 'dataDomain'
  | 'exceptionType'
  | 'businessArea'
  | 'visibleToRole'
  | 'productSearch'
  | 'productStatus'
  | 'productType'
  | 'mainCategory'
  | 'inventoryTracking'
  | 'stock'
  | 'lastSoldAging'
  | 'xeroSalesAccount'
  | 'xeroSalesAccountCode'
  | 'xeroPurchaseAccount'
  | 'xeroPurchaseAccountCode'
  | 'supplierMultiSelect'

// Filter values interface - all fields optional
export interface ReportFilters {
  search?: string
  textSearch?: string
  fpStatus?: string
  market?: string
  region?: string
  romFbc?: string
  systemStatus?: string
  adoptionStatus?: string
  icuStatus?: string
  sellingStatus?: string
  startDate?: string
  endDate?: string
  system?: string
  syncStatus?: string
  initiatedBy?: string
  connectionStatus?: string
  exceptionTypes?: string
  minGapDays?: string
  minVarianceAmount?: string
  dataDomain?: string
  exceptionType?: string
  businessArea?: string
  visibleToRole?: string
  productSearch?: string
  productStatus?: string
  productType?: string
  mainCategory?: string
  inventoryTracking?: string
  stock?: string
  lastSoldAging?: string
  xeroSalesAccount?: string
  xeroSalesAccountCode?: string
  xeroPurchaseAccount?: string
  xeroPurchaseAccountCode?: string
  supplierMultiSelect?: string  // comma-joined supplier names
}

// Configuration for the useReportFilters hook
export interface ReportFilterConfig {
  enabledFilters: FilterKey[]
  defaults?: Partial<ReportFilters>
  syncToUrl?: boolean
}

// Configuration for linking filter behaviors
export interface FilterLinkConfig {
  linkFpStatusToSearch?: boolean  // When true, FP Status dropdown filters the FP search dropdown results
}

// Active filter for display chips
export interface ActiveFilter {
  key: FilterKey
  label: string
  value: string
}

// Region option from API
export interface RegionOption {
  region_code: string
  region_name: string
  market: string
}

// BDM/ROM/FBC option from API
export interface BdmOption {
  id: number
  name: string
  market: string
}

// FP Status options
export const FP_STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'Onboarding', label: 'Onboarding' },
  { value: 'Active', label: 'Active' },
  { value: 'Terminated', label: 'Terminated' },
  { value: 'Test', label: 'Test' },
]

// System Status options (matches backend SystemStatus enum)
export const SYSTEM_STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'None', label: 'None' },
  { value: 'Provisioning', label: 'Provisioning' },
  { value: 'Live', label: 'Live' },
  { value: 'Retiring', label: 'Retiring' },
  { value: 'Ended', label: 'Ended' },
]

// Adoption Status options
export const ADOPTION_STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'Unknown', label: 'Unknown' },
  { value: 'None', label: 'None' },
  { value: 'Partial', label: 'Partial' },
  { value: 'Full', label: 'Full' },
]

// System options (Lightspeed, Xero, Skimmer)
export const SYSTEM_OPTIONS = [
  { value: '', label: 'All Systems' },
  { value: 'lightspeed', label: 'Lightspeed' },
  { value: 'xero', label: 'Xero' },
  { value: 'skimmer', label: 'Skimmer' },
]

// Sync Status options
export const SYNC_STATUS_OPTIONS = [
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' },
]

// ICU Status options
export const ICU_STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
]

// Selling Status options
export const SELLING_STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
]

// Connection Status options
export const CONNECTION_STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'error', label: 'Error' },
  { value: 'reauthorization_required', label: 'Re-auth Required' },
]

// Filter label mappings for display
export const FILTER_LABELS: Record<FilterKey, string> = {
  search: 'Search',
  textSearch: 'Search',
  fpStatus: 'FP Status',
  market: 'Market',
  region: 'Region',
  romFbc: 'ROM/FBC',
  systemStatus: 'Platform Status',
  adoptionStatus: 'Platform Adoption',
  icuStatus: 'ICU',
  sellingStatus: 'Selling',
  dateRange: 'Date Range',
  system: 'System',
  syncStatus: 'Status',
  initiatedBy: 'Initiated By',
  connectionStatus: 'Connection Status',
  exceptionTypes: 'Exception Type',
  minGapDays: 'Min Gap (Days)',
  minVarianceAmount: 'Min Variance ($)',
  dataDomain: 'Data Domain',
  exceptionType: 'Exception Type',
  businessArea: 'Business Area',
  visibleToRole: 'Visible To',
  productSearch: 'Name or SKU',
  productStatus: 'Product Status',
  productType: 'Product Type',
  mainCategory: 'Main Category',
  inventoryTracking: 'Inventory Tracking',
  stock: 'Stock',
  lastSoldAging: 'Last Sold',
  xeroSalesAccount: 'Xero Sales Account',
  xeroSalesAccountCode: 'Sales Code',
  xeroPurchaseAccount: 'Xero Purchase Account',
  xeroPurchaseAccountCode: 'Purchase Code',
  supplierMultiSelect: 'Suppliers',
}

// Data Domain options (mirrors backend allowed_domains in admin_exception_types schema)
export const DATA_DOMAIN_OPTIONS = [
  { value: '', label: 'All Domains' },
  { value: 'sale', label: 'Sales' },
  { value: 'stock_management', label: 'Stock Management' },
  { value: 'product_setup', label: 'Product Setup' },
  { value: 'product_pricing', label: 'Product Pricing' },
  { value: 'client', label: 'Client' },
  { value: 'system', label: 'System' },
]

// Exception Type options (mirrors backend EXCEPTION_TYPES in admin_exception_types schema)
export const EXCEPTION_TYPE_OPTIONS = [
  { value: '', label: 'Any Type' },
  { value: 'process_issue', label: 'Process Issue' },
  { value: 'data_discrepancy', label: 'Data Discrepancy' },
]

// Business Area options (mirrors backend BUSINESS_AREAS in admin_exception_types schema)
export const BUSINESS_AREA_OPTIONS = [
  { value: '', label: 'Any Area' },
  { value: 'operations', label: 'Operations' },
  { value: 'it', label: 'IT' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'finance', label: 'Finance' },
  { value: 'support', label: 'Support' },
  { value: 'other', label: 'Other' },
]

// Visible-to-role options (mirrors UserRole enum)
export const VISIBLE_TO_ROLE_OPTIONS = [
  { value: '', label: 'Any Role' },
  { value: 'EVO_ADMIN', label: 'Evo Admin' },
  { value: 'EVO_TEAM', label: 'Evo Team' },
  { value: 'PSC_TEAM', label: 'PSC Team' },
  { value: 'FP_USER', label: 'FP User' },
]

// Min Gap (Days) options for the Backdated Lightspeed Sales report
export const MIN_GAP_DAYS_OPTIONS = [
  { value: '1', label: '1+ days (any backdating)' },
  { value: '3', label: '3+ days' },
  { value: '7', label: '7+ days (1+ week)' },
  { value: '14', label: '14+ days (2+ weeks)' },
  { value: '30', label: '30+ days (1+ month)' },
  { value: '90', label: '90+ days (3+ months)' },
]

// Product Status options (cross-FP catalogue reports)
export const PRODUCT_STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
]

// Product Type options - values mirror shared.inspector.classifiers keys
export const PRODUCT_TYPE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'simple', label: 'Standard' },
  { value: 'variant_parent', label: 'Parent Variant' },
  { value: 'variant_child', label: 'Variant' },
  { value: 'composite_kit', label: 'Kit' },
  { value: 'composite_dosage', label: 'Dosage' },
]

// Inventory Tracking options
export const INVENTORY_TRACKING_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'on', label: 'On' },
  { value: 'off', label: 'Off' },
]

// Stock band options
export const STOCK_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'on_hand', label: 'Stock on hand' },
  { value: 'no_stock', label: 'No stock' },
  { value: 'negative', label: 'Negative stock' },
]

// Last Sold aging bucket options (mirrors product_catalogue_review aging buckets)
export const LAST_SOLD_AGING_OPTIONS = [
  { value: '', label: 'All' },
  { value: '0-3m', label: '0-3 months' },
  { value: '3-6m', label: '3-6 months' },
  { value: '6-12m', label: '6-12 months' },
  { value: '12-24m', label: '12-24 months' },
  { value: 'over_24m', label: 'Over 24 months' },
  { value: 'never', label: 'Never sold' },
]

// Xero account state filter options. "specific" reveals a code input with
// market-scoped autocomplete; "empty" matches NULL OR codes that fail
// XERO_ACCOUNT_VALID_REGEX (parity with tag_inv_ls_xero_account_error -
// e.g. "TBD" or "8000A" surface under Empty).
export const XERO_ACCOUNT_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'set', label: 'Filled' },
  { value: 'missing', label: 'Empty' },
  { value: 'specific', label: 'Specific code...' },
]
